import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch,useSelector } from 'react-redux';
import axios from 'axios';
import i18next from 'i18next';

import {setCurrentCompany} from '../store/currentCompanySlice.js'
import {setCurrentBranch} from '../store/currentBranchSlice.js'
import {getCompanyById} from '../utils/tenantManager.js'
import {setSettings} from '../store/settingSlice.js'
import {setLang} from '../store/langSlice.js'

import {arrayNameByLang,arrayToObjKeyValue, appStorageKey} from '../utils/global.js'
import { CENTRAL_SETTINGS_CACHE_EVENT, buildCentralSettingsCacheKey } from '../utils/centralSettingsCache.js';
import useLicense from './useLicense.js';

const CENTRAL_SETTINGS_SCOPE = 'global';
const CENTRAL_SELECTION_SCOPE = 'header-selection';

const useStartup = () => {
    
    const dispatch = useDispatch();
    const [startup,setStartup] = useState(false);
    const user = useSelector(state => state.auth).user
    const lang = useSelector(state => state.lang)
    const currentCompanyState = useSelector(state => state.currentCompany);
    const currentBranchState = useSelector(state => state.currentBranch);
    const currentSettingsState = useSelector(state => state.settings);

    const safeParse = (value) => {
        if (!value) return null;
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    };
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const waitForToken = useCallback(async () => {
        for (let attempt = 0; attempt < 6; attempt += 1) {
            const token = localStorage.getItem('token');
            if (token) return token;
            await sleep(250 * (attempt + 1));
        }
        return null;
    }, []);
    const userSettings = arrayToObjKeyValue(safeParse(localStorage.getItem('user'))?.userSettings);
    const normalizeLang = (value) => String(value || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const getStoredPreferredLang = () => {
        try {
            return localStorage.getItem('preferredLang') || localStorage.getItem('i18nextLng') || localStorage.getItem('appLang');
        } catch {
            return null;
        }
    };
    const [centralLang, setCentralLang] = useState(null);
    const [centralSelection, setCentralSelection] = useState({ companyId: null, branchId: null });
    const getNameByLang = (nameList, preferredLang = 'en') => {
        const currentLang = String(preferredLang || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
        const fallbackLang = currentLang === 'ar' ? 'en' : 'ar';
        return (
            arrayNameByLang(nameList, currentLang)?.text ||
            arrayNameByLang(nameList, fallbackLang)?.text ||
            nameList?.[0]?.text ||
            ''
        );
    };

    const userCompany = getCompanyById(user?.company_id)
    const storedCompany = safeParse(localStorage.getItem(appStorageKey('selectedCompany')));
    const storedCompanyId = storedCompany?.value;
    const storedCompanyValid = storedCompanyId && getCompanyById(storedCompanyId);
    const centralCompanyId = centralSelection?.companyId;
    const centralCompany = centralCompanyId ? getCompanyById(centralCompanyId) : null;
    const selectedCompany = storedCompanyValid || centralCompany || userCompany;
    const selectedUiLang = String(lang?.value || getStoredPreferredLang() || centralLang || userSettings?.DEFAULT_LANG || 'en')
        .toLowerCase()
        .startsWith('ar') ? 'ar' : 'en';
    const selectedCompanyName = selectedCompany && (
        getNameByLang(selectedCompany?.name, selectedUiLang) ||
        'Company'
    );

    const userBranch = userCompany?.branches?.find((branch) => branch?._id === user?.branch_id);
    const storedBranch = safeParse(localStorage.getItem(appStorageKey('selectedBranch')));
    const storedBranchId = storedBranch?.value;
    const companyBranches = selectedCompany?.branches || [];
    const storedBranchValid = storedBranchId && companyBranches?.some((branch) => branch?._id === storedBranchId);
    const centralBranchId = centralSelection?.branchId;
    const centralBranchValid = centralBranchId && companyBranches?.some((branch) => branch?._id === centralBranchId);
    const selectedBranch = storedBranchValid
        ? companyBranches?.find((branch) => branch?._id === storedBranchId)
        : centralBranchValid
        ? companyBranches?.find((branch) => branch?._id === centralBranchId)
        : (selectedCompany?._id === userCompany?._id ? userBranch : companyBranches?.[0]);
    const selectedBranchName = selectedBranch && (
        getNameByLang(selectedBranch?.name, selectedUiLang) ||
        'Branch'
    );
    const canReadCentralSettings = Boolean(selectedCompany?._id && user?.tenant_id);

    const invoiceSettings = selectedCompany ? arrayToObjKeyValue(selectedCompany?.settings?.invoice) : null;
    const generalSettings = selectedCompany ? arrayToObjKeyValue(selectedCompany?.settings?.generalSettings) : null;
    const quoteSettings = selectedCompany ? arrayToObjKeyValue(selectedCompany?.settings?.quote) : null;
    const normalizeSettingsBlock = useCallback((value) => {
        if (!value) return {};
        if (Array.isArray(value)) return arrayToObjKeyValue(value);
        return value;
    }, []);
    const resolveSettings = useCallback((centralValue, fallbackValue) => {
        const normalizedFallback = normalizeSettingsBlock(fallbackValue);
        const normalizedCentral = normalizeSettingsBlock(centralValue);
        if (normalizedCentral && Object.keys(normalizedCentral).length) {
            return { ...normalizedFallback, ...normalizedCentral };
        }
        return normalizedFallback || {};
    }, [normalizeSettingsBlock]);
    const centralSettingsCacheKey = buildCentralSettingsCacheKey(
        user?.tenant_id,
        selectedCompany?._id,
        CENTRAL_SETTINGS_SCOPE
    );
    const initialCentralSettings = useMemo(() => {
        if (!centralSettingsCacheKey) return null;
        const cachedRaw = safeParse(localStorage.getItem(centralSettingsCacheKey));
        return cachedRaw?.data || null;
    }, [centralSettingsCacheKey]);
    const [centralSettings, setCentralSettings] = useState(initialCentralSettings);
    useEffect(() => {
        if (!centralSettingsCacheKey || typeof window === 'undefined') return;

        const handleCentralSettingsUpdated = (event) => {
            if (event?.detail?.cacheKey !== centralSettingsCacheKey) return;
            const nextData = event?.detail?.data || null;
            setCentralSettings((prevValue) => {
                const prevSignature = JSON.stringify(prevValue || null);
                const nextSignature = JSON.stringify(nextData || null);
                return prevSignature === nextSignature ? prevValue : nextData;
            });
        };

        window.addEventListener(CENTRAL_SETTINGS_CACHE_EVENT, handleCentralSettingsUpdated);
        return () => window.removeEventListener(CENTRAL_SETTINGS_CACHE_EVENT, handleCentralSettingsUpdated);
    }, [centralSettingsCacheKey]);
    const mergedSettings = useMemo(() => ({
        invoiceSettings: resolveSettings(centralSettings?.invoiceSettings, invoiceSettings),
        generalSettings: resolveSettings(centralSettings?.generalSettings, generalSettings),
        quoteSettings: resolveSettings(centralSettings?.quoteSettings, quoteSettings)
    }), [centralSettings, generalSettings, invoiceSettings, quoteSettings, resolveSettings]);
    const nextSettingsPayload = useMemo(() => ({
        invoiceSettings: mergedSettings.invoiceSettings || {},
        generalSettings: mergedSettings.generalSettings || {},
        quoteSettings: mergedSettings.quoteSettings || {}
    }), [mergedSettings.generalSettings, mergedSettings.invoiceSettings, mergedSettings.quoteSettings]);
    const currentSettingsSignature = useMemo(
        () => JSON.stringify({
            invoiceSettings: currentSettingsState?.invoiceSettings || {},
            generalSettings: currentSettingsState?.generalSettings || {},
            quoteSettings: currentSettingsState?.quoteSettings || {}
        }),
        [currentSettingsState?.generalSettings, currentSettingsState?.invoiceSettings, currentSettingsState?.quoteSettings]
    );
    const nextSettingsSignature = useMemo(
        () => JSON.stringify(nextSettingsPayload),
        [nextSettingsPayload]
    );
    const DEFAULT_LANG = mergedSettings?.generalSettings?.DEFAULT_LANG || userSettings?.DEFAULT_LANG || 'en';
    const langToApply = useMemo(() => {
        const langFromCentral = centralLang ? normalizeLang(centralLang) : null;
        const langFromState = lang?.value ? normalizeLang(lang.value) : null;
        const langFromStorage = getStoredPreferredLang();
        const normalizedFromStorage = langFromStorage ? normalizeLang(langFromStorage) : null;
        return langFromState || normalizedFromStorage || langFromCentral || normalizeLang(DEFAULT_LANG);
    }, [DEFAULT_LANG, centralLang, lang?.value]);
    
    const {data:dataLicense} = useLicense();

    useEffect(() =>{
        const pathArray = window.location.pathname.split('/');
        if(pathArray.length > 2 && pathArray[pathArray.length-2] === 'token'){
            setStartup(true)
        }
    },[dataLicense]);
    
    useEffect(()=>{
        if(selectedCompany){
            if (lang?.value !== langToApply) {
                dispatch(setLang({
                    label: langToApply === 'ar' ? 'عربي' : langToApply === 'en' ? 'English' : langToApply,
                    value: langToApply
                }));
            }

            if (i18next.language !== langToApply) {
                i18next.changeLanguage(langToApply);
            }

            if (
                currentCompanyState?.value !== selectedCompany._id ||
                currentCompanyState?.name !== selectedCompanyName
            ) {
                dispatch(setCurrentCompany({
                    name:selectedCompanyName,
                    value:selectedCompany._id
                }));
            }

            const branchNameToApply = selectedBranchName || (langToApply === 'ar' ? 'الفرع' : 'Select a Branch');
            if (
                currentBranchState?.value !== selectedBranch?._id ||
                currentBranchState?.name !== branchNameToApply
            ) {
                dispatch(setCurrentBranch({
                    name: branchNameToApply,
                    value: selectedBranch?._id
                }));
            }

            if (currentSettingsSignature !== nextSettingsSignature) {
                dispatch(setSettings(nextSettingsPayload));
            }

            if (!startup) {
                setStartup(true)
            }
        }

    },[currentBranchState?.name, currentBranchState?.value, currentCompanyState?.name, currentCompanyState?.value, currentSettingsSignature, dispatch, lang?.value, langToApply, nextSettingsPayload, nextSettingsSignature, selectedBranch?._id, selectedBranchName, selectedCompany?._id, selectedCompanyName, startup]);

    useEffect(() => {
        const fetchCentralSettings = async () => {
            if (!selectedCompany?._id || !user?.tenant_id) return;
            if (!canReadCentralSettings) {
                setCentralSettings(initialCentralSettings || null);
                return;
            }
            const token = await waitForToken();
            if (!token) return;
            const cacheKey = centralSettingsCacheKey;
            if (!cacheKey) return;
            const cachedRaw = safeParse(localStorage.getItem(cacheKey));
            if (cachedRaw?.data) {
                const currentSignature = JSON.stringify(centralSettings || null);
                const nextSignature = JSON.stringify(cachedRaw.data || null);
                if (currentSignature !== nextSignature) {
                    setCentralSettings(cachedRaw.data);
                }
            }
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&companyId=${selectedCompany._id}&scope=${CENTRAL_SETTINGS_SCOPE}`;
            try {
                const headers = {
                    Authorization: `Bearer ${token}`
                };
                if (cachedRaw?.version) {
                    headers['If-None-Match'] = String(cachedRaw.version);
                }
                const response = await axios.get(url, { headers, validateStatus: () => true });
                if (response.status === 304) {
                    return;
                }
                if (response?.data?.status === 1) {
                    const nextData = response?.data?.data || null;
                    const nextVersion = response?.data?.version ?? cachedRaw?.version ?? 0;
                    const currentSignature = JSON.stringify(centralSettings || null);
                    const nextSignature = JSON.stringify(nextData || null);
                    if (currentSignature !== nextSignature) {
                        setCentralSettings(nextData);
                    }
                    localStorage.setItem(cacheKey, JSON.stringify({ data: nextData, version: nextVersion }));
                    return;
                }
                if (response?.status === 403 || response?.status === 401) {
                    const fallbackData = cachedRaw?.data || initialCentralSettings || null;
                    const currentSignature = JSON.stringify(centralSettings || null);
                    const nextSignature = JSON.stringify(fallbackData || null);
                    if (currentSignature !== nextSignature) {
                        setCentralSettings(fallbackData);
                    }
                }
            } catch (error) {
            }
        };
        fetchCentralSettings();
    }, [canReadCentralSettings, centralSettings, centralSettingsCacheKey, initialCentralSettings, selectedCompany?._id, user?.tenant_id]);

    useEffect(() => {
        const fetchCentralSelection = async () => {
            if (!user?.tenant_id) return;
            const token = await waitForToken();
            if (!token) return;
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&scope=${CENTRAL_SELECTION_SCOPE}`;
            try {
                const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true });
                if (response?.data?.status !== 1) {
                    setCentralLang(null);
                    return;
                }
                const selection = response?.data?.data;
                const selectedLang = selection?.selectedLang || selection?.lang || selection?.language || response?.data?.currentLanguage;
                setCentralLang(selectedLang || null);

                const selectedCompanyId = selection?.selectedCompanyId || selection?.companyId;
                const selectedBranchId = selection?.selectedBranchId || selection?.branchId;
                setCentralSelection({
                    companyId: selectedCompanyId || null,
                    branchId: selectedBranchId || null
                });
                const normalizedLang = normalizeLang(selectedLang || lang?.value || userSettings?.DEFAULT_LANG || 'en');

                if (selectedCompanyId) {
                    const companyEntity = getCompanyById(selectedCompanyId);
                    const nextCompanyName = companyEntity ? getNameByLang(companyEntity?.name, normalizedLang) : null;
                    dispatch(setCurrentCompany({
                        name: nextCompanyName || (normalizedLang === 'ar' ? 'الشركة' : 'Company'),
                        value: selectedCompanyId
                    }));

                    if (selectedBranchId) {
                        const branchEntity = companyEntity?.branches?.find((branch) => String(branch?._id) === String(selectedBranchId));
                        const nextBranchName = branchEntity ? getNameByLang(branchEntity?.name, normalizedLang) : null;
                        dispatch(setCurrentBranch({
                            name: nextBranchName || (normalizedLang === 'ar' ? 'الفرع' : 'Branch'),
                            value: selectedBranchId
                        }));
                    }
                }
            } catch (error) {
                setCentralLang(null);
            }
        };
        fetchCentralSelection();
    }, [user?.tenant_id, waitForToken]);

    return {startup}
}

export default useStartup
